import styled, {
  css,
  DefaultTheme,
  ThemedStyledProps,
} from 'styled-components';
import { getThemeColor } from 'utils/styled';
import { SwitchLabelStyleProps, SwitchStyleProps } from './types';
import { createSwitchTransition } from './utils';

// desktop constants
const TRACK_WIDTH_PX_DESKTOP = 50;
const THUMB_WIDTH_PX_DESKTOP = 20;
const THUMB_MARGIN_PX_DESKTOP = 2;
const THUMB_SELECTED_TRANSLATION_PX_DESKTOP =
  TRACK_WIDTH_PX_DESKTOP - THUMB_WIDTH_PX_DESKTOP - THUMB_MARGIN_PX_DESKTOP * 2;

// mobile constants
const TRACK_WIDTH_PX_MOBILE = 39;
const THUMB_WIDTH_PX_MOBILE = 22;
const THUMB_MARGIN_PX_MOBILE = 1;
const THUMB_SELECTED_TRANSLATION_PX_MOBILE =
  TRACK_WIDTH_PX_MOBILE - THUMB_WIDTH_PX_MOBILE - THUMB_MARGIN_PX_MOBILE * 2;

const createMeasurementGetter =
  (mobileValue: number, desktopValue: number) =>
  (props: ThemedStyledProps<SwitchStyleProps, DefaultTheme>) => {
    const { $isMobile } = props;
    return `${$isMobile ? mobileValue : desktopValue}px`;
  };

const trackWidth = createMeasurementGetter(
  TRACK_WIDTH_PX_MOBILE,
  TRACK_WIDTH_PX_DESKTOP,
);

const thumbMargin = createMeasurementGetter(
  THUMB_MARGIN_PX_MOBILE,
  THUMB_MARGIN_PX_DESKTOP,
);

const thumbWidth = createMeasurementGetter(
  THUMB_WIDTH_PX_MOBILE,
  THUMB_WIDTH_PX_DESKTOP,
);

const thumbTransitionPx = createMeasurementGetter(
  THUMB_SELECTED_TRANSLATION_PX_MOBILE,
  THUMB_SELECTED_TRANSLATION_PX_DESKTOP,
);

function trackBackgroundColor(
  props: ThemedStyledProps<SwitchStyleProps, DefaultTheme>,
) {
  const { $isSelected } = props;
  return $isSelected ? getThemeColor('actionPrimary') : getThemeColor('s1');
}

function thumbTransform(
  props: ThemedStyledProps<SwitchStyleProps, DefaultTheme>,
) {
  const { $isSelected } = props;
  return $isSelected ? css`translateX(${thumbTransitionPx})` : 'translateX(0)';
}

function containerFlexDirection(
  props: ThemedStyledProps<SwitchLabelStyleProps, DefaultTheme>,
) {
  const { $labelPosition } = props;
  return $labelPosition === 'before' ? 'row-reverse' : 'row';
}

export const Root = styled.label<SwitchLabelStyleProps>`
  align-items: center;
  display: inline-flex;
  cursor: pointer;
  flex-direction: ${containerFlexDirection};

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    cursor: not-allowed;
    opacity: 0.25;
  `}
`;

export const Track = styled.div<SwitchStyleProps>`
  align-items: center;
  background-color: ${trackBackgroundColor};
  border-radius: 1000px;
  display: flex;
  height: 24px;
  padding: 2px;
  position: relative;
  width: ${trackWidth};

  ${createSwitchTransition('background-color')};
`;

export const Thumb = styled.div<SwitchStyleProps>`
  background-color: ${getThemeColor('white')};
  border-radius: 50%;
  height: ${thumbWidth};
  left: ${thumbMargin};
  position: absolute;
  transform: ${thumbTransform};
  width: ${thumbWidth};

  ${createSwitchTransition('transform')};
`;
